import { useEffect, useState } from "react";
import { playClip, unlockAudio, CHARACTER_META } from "@/lib/audio";
import { Volume2, Mic } from "lucide-react";

/**
 * PresenterBubble — animated circular presenter frame with a real
 * character photo. Pulses in sync with HTMLAudio start/end events.
 *
 * Props:
 *  - character: "amanda" | "alfred" | "bentley" | "wingman"
 *  - clip: string clip id to play (e.g. "mission", "philosophy", "welcome")
 *  - name:  optional display name override
 *  - size:  px (default 132)
 *  - autoPlay: fire the clip once on mount (after audio gate)
 *  - gateReady: only autoplay when the Grid audio gate is unlocked
 *  - interrupt: whether the play call cancels other playback (default false for autoplay)
 */
export default function PresenterBubble({
  character = "amanda",
  clip,
  name,
  size = 132,
  autoPlay = false,
  gateReady = false,
  className = "",
}) {
  const [speaking, setSpeaking] = useState(false);
  const [avatarOk, setAvatarOk] = useState(true);
  const meta = CHARACTER_META[character] || CHARACTER_META.amanda;
  const displayName = name || meta.name;
  const avatarSrc = meta.avatar;

  useEffect(() => {
    if (!autoPlay || !gateReady) return;
    const t = setTimeout(() => {
      playClip(character, clip, {
        interrupt: false,   // queue behaviour — don't cut off Wingman etc.
        onStart: () => setSpeaking(true),
        onEnd: () => setSpeaking(false),
      });
    }, 400);
    return () => clearTimeout(t);
  }, [autoPlay, gateReady, character, clip]);

  const trigger = () => {
    unlockAudio();
    playClip(character, clip, {
      onStart: () => setSpeaking(true),
      onEnd: () => setSpeaking(false),
    });
  };

  return (
    <div
      className={`inline-flex flex-col items-center gap-2 ${className}`}
      data-testid={`presenter-bubble-${character}`}
    >
      <button
        type="button"
        onClick={trigger}
        aria-label={`Play ${displayName} briefing`}
        data-testid={`presenter-bubble-trigger-${character}`}
        className="relative rounded-full focus:outline-none focus:ring-2 focus:ring-gold/60"
        style={{ width: size, height: size }}
      >
        {/* Rotating gradient border (active when speaking) */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            padding: 3,
            background:
              "conic-gradient(from 0deg, #D4AF37 0%, #34D399 35%, #D4AF37 70%, #34D399 100%)",
            animation: speaking ? "presenter-spin 3.6s linear infinite" : "none",
            opacity: 1,
            boxShadow: speaking
              ? "0 0 34px rgba(212,175,55,0.6), 0 0 60px rgba(52,211,153,0.4)"
              : "0 0 18px rgba(212,175,55,0.3)",
            transition: "box-shadow 300ms ease",
          }}
          aria-hidden
        >
          <div
            className="w-full h-full rounded-full overflow-hidden"
            style={{ background: "rgba(6,11,20,0.95)" }}
          >
            {avatarOk ? (
              <img
                src={avatarSrc}
                alt={displayName}
                loading="lazy"
                onError={() => setAvatarOk(false)}
                className="w-full h-full object-cover"
                style={{ display: "block" }}
                data-testid={`presenter-avatar-${character}`}
              />
            ) : (
              // Graceful fallback if the image 404s — a stylised initial.
              <div
                className="w-full h-full flex items-center justify-center font-display text-gold"
                style={{ fontSize: Math.floor(size * 0.35) }}
              >
                {displayName.charAt(0)}
              </div>
            )}
          </div>
        </div>

        {/* Pulsing halo (only visible when speaking) */}
        {speaking && (
          <>
            <span
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                border: "1px solid rgba(212,175,55,0.75)",
                animation: "presenter-halo 1.6s ease-out infinite",
              }}
              aria-hidden
            />
            <span
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                border: "1px solid rgba(52,211,153,0.6)",
                animation: "presenter-halo 1.6s ease-out infinite 400ms",
              }}
              aria-hidden
            />
          </>
        )}

        {/* Mic pip */}
        <span
          className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full border flex items-center justify-center"
          style={{
            borderColor: speaking ? "#D4AF37" : "rgba(255,255,255,0.25)",
            background: speaking ? "rgba(212,175,55,0.28)" : "rgba(6,11,20,0.92)",
            color: speaking ? "#D4AF37" : "#94A3B8",
            boxShadow: speaking ? "0 0 14px rgba(212,175,55,0.65)" : "none",
          }}
        >
          {speaking ? <Volume2 size={13} /> : <Mic size={13} />}
        </span>
      </button>

      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] font-mono-aviation text-slate-400">
          {speaking ? "On Air" : meta.role || "Presenter"}
        </div>
        <div className="font-display text-white text-sm leading-tight">
          {displayName}
        </div>
      </div>

      <style>{`
        @keyframes presenter-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes presenter-halo {
          0%   { transform: scale(1);   opacity: 0.9; }
          100% { transform: scale(1.4); opacity: 0;   }
        }
      `}</style>
    </div>
  );
}
